/* 511c826a: pure view model for Bambu print actions on the provider
   order page — status normalization, RU labels/badges, the control
   button state machine (mirrors ProviderBambuPrintService guards),
   progress display and the send-to-print pickers (printer + completed
   slicing artifact). No React, no fetch: mirrored by
   tests/printJobsView.test.ts on the node test runner. */

import type { SlicingJob, SlicingPrinter } from '../types/slicing';
import {
  PRINT_JOB_STATUS_LABELS,
  printJobStatusBadgeClass,
  normalizePrintJobStatus,
} from '../types/printing.ts';
import type { BambuPrintJob, PrintJobStatus } from '../types/printing';
import { plateLabel } from './slicingPlatesView.ts';

/* ── status chrome (labels live in types/printing.ts next to the union) ── */

export { PRINT_JOB_STATUS_LABELS, printJobStatusBadgeClass, normalizePrintJobStatus };

/* ── one print-job row ── */

export interface PrintJobRowView {
  status: PrintJobStatus;
  statusLabel: string;
  badgeClass: string;
  /** Clamped 0–100 for the thin progress bar; null = no live report yet. */
  progressPercent: number | null;
  /** «42%» chip text; null when there is nothing truthful to show. */
  progressLabel: string | null;
  /** Control availability — mirrors the backend state machine exactly. */
  canPause: boolean;
  canResume: boolean;
  canStop: boolean;
  /** Live refresh makes sense only while the printer may still be moving. */
  canRefresh: boolean;
  /** Failure reason (send/stop failures, printer-reported errors). */
  failureText: string | null;
}

/**
 * ProviderBambuPrintService.ControlPrintJobAsync allows: pause ← Printing,
 * resume ← Paused, stop ← Queued/Printing/Paused. The row must never offer
 * a button the API would reject with a guard error.
 */
export function buildPrintJobRowView(job: BambuPrintJob): PrintJobRowView {
  const status = normalizePrintJobStatus(job.status);
  const percent = job.percent;
  const live = status === 'Printing' || status === 'Paused';
  const progressPercent =
    live && typeof percent === 'number' && Number.isFinite(percent) && percent >= 0
      ? Math.min(100, Math.round(percent))
      : null;
  return {
    status,
    statusLabel: PRINT_JOB_STATUS_LABELS[status],
    badgeClass: printJobStatusBadgeClass(status),
    progressPercent,
    progressLabel: progressPercent === null ? null : `${progressPercent}%`,
    canPause: status === 'Printing',
    canResume: status === 'Paused',
    canStop: status === 'Queued' || status === 'Printing' || status === 'Paused',
    canRefresh: live,
    failureText:
      status === 'Failed' && (job.errorMessage ?? '').trim().length > 0
        ? job.errorMessage!.trim()
        : null,
  };
}

/* ── send-to-print pickers ── */

/** Completed slicing artifacts of one printer — the only sendable inputs (backend guard). */
export function sendableArtifacts(slicingJobs: SlicingJob[], printerId: string): SlicingJob[] {
  return slicingJobs.filter(
    (j) => j.printerId === printerId && j.hasArtifact && j.status === 'Completed',
  );
}

/**
 * Default printer for the picker: the first printer with a completed
 * artifact (send is possible right away), else the first printer at all —
 * the select still renders and the artifact list explains itself.
 */
export function defaultSendPrinter(
  printers: SlicingPrinter[],
  slicingJobs: SlicingJob[],
): string {
  const withArtifact = printers.find((p) => sendableArtifacts(slicingJobs, p.printerId).length > 0);
  return (withArtifact ?? printers[0])?.printerId ?? '';
}

export interface SendArtifactOption {
  slicingJobId: string;
  /** «detail.gcode.3mf · Пластина 2» — file name the provider recognizes. */
  label: string;
}

/** Picker options for the selected printer, newest-first (same order the slicing list shows). */
export function sendArtifactOptions(slicingJobs: SlicingJob[], printerId: string): SendArtifactOption[] {
  return sendableArtifacts(slicingJobs, printerId).map((j) => ({
    slicingJobId: j.jobId,
    label:
      j.plateIndex != null && j.plateIndex > 0
        ? `${j.modelFileName} · ${plateLabel(j.plateIndex)}`
        : j.modelFileName,
  }));
}

export interface SendFormView {
  /** Both picks present — the button is enabled (printer⇄artifact mismatch is impossible: options are filtered per printer). */
  canSend: boolean;
  /** Honest empty-state line when nothing can be sent yet. */
  hint: string | null;
}

export function buildSendFormView(
  printers: SlicingPrinter[],
  slicingJobs: SlicingJob[],
  printerId: string,
  slicingJobId: string,
): SendFormView {
  const canSend = printerId.length > 0 && slicingJobId.length > 0;
  if (canSend) return { canSend: true, hint: null };
  const anyArtifact = printers.some((p) => sendableArtifacts(slicingJobs, p.printerId).length > 0);
  if (printers.length === 0) {
    return { canSend: false, hint: 'Добавьте принтер Bambu в кабинете, чтобы отправлять файлы на печать.' };
  }
  if (!anyArtifact) {
    return { canSend: false, hint: 'Сначала подготовьте g-code — готовые файлы можно отправить прямо на принтер.' };
  }
  return { canSend: false, hint: 'Выберите принтер и подготовленный файл печати.' };
}
